import { useState } from 'react';
import { AlertCircle, Monitor, Smartphone, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useProfileSessions,
  useRevokeSession,
  type ProfileSession,
} from '@/lib/api/profile';
import { useRevokeAllSessions } from '@/lib/api/auth';
import { ApiError } from '@/lib/api';

/**
 * Раздел «Активные сессии» — list + revoke.
 *
 * Backend:
 *   GET    /profile/me/sessions   → { items, total }
 *   DELETE /profile/me/sessions/{id}
 *   DELETE /profile/me/sessions      — все сразу, включая текущую (AK-469)
 *
 * Текущую сессию (is_current_session=true) поштучно revoke не даём — иначе
 * юзер мгновенно вылетит по неосторожному клику. Для logout есть кнопка в
 * footer drawer'а, а для «подозреваю угон» — «Выйти со всех устройств» внизу
 * с подтверждением.
 */
export function ProfileSectionSessions() {
  const sessionsQuery = useProfileSessions();
  const revokeMut = useRevokeSession();
  const revokeAllMut = useRevokeAllSessions();
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmAllOpen, setConfirmAllOpen] = useState(false);

  const handleRevokeAll = async () => {
    setError(null);
    try {
      await revokeAllMut.mutateAsync();
      // Своя сессия тоже отозвана — остаёмся на странице только до редиректа.
      window.location.assign('/login');
    } catch (err) {
      setConfirmAllOpen(false);
      if (err instanceof ApiError) setError(err.detail);
      else setError('Не удалось завершить сессии');
    }
  };

  const revokeAllBlock = (
    <>
      <div className="pt-1">
        <Button
          type="button"
          variant="destructive"
          size="sm"
          className="w-full sm:w-auto"
          onClick={() => setConfirmAllOpen(true)}
          disabled={revokeAllMut.isPending}
        >
          Выйти со всех устройств
        </Button>
        <p className="mt-1 text-xs text-muted-foreground">
          Завершит все сессии, включая текущую — придётся войти заново.
        </p>
      </div>

      <Dialog open={confirmAllOpen} onOpenChange={setConfirmAllOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Выйти со всех устройств?</DialogTitle>
            <DialogDescription>
              Все сессии будут завершены, включая эту. Другие аккаунты из меню
              профиля тоже отвяжутся — в каждый нужно будет войти заново.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmAllOpen(false)}
              disabled={revokeAllMut.isPending}
            >
              Отмена
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => void handleRevokeAll()}
              disabled={revokeAllMut.isPending}
            >
              {revokeAllMut.isPending ? 'Завершаем…' : 'Выйти со всех'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  const handleRevoke = async (s: ProfileSession) => {
    setError(null);
    setRevokingId(s.id);
    try {
      await revokeMut.mutateAsync(s.id);
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail);
      else setError('Не удалось завершить сессию');
    } finally {
      setRevokingId(null);
    }
  };

  if (sessionsQuery.isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
      </div>
    );
  }

  if (sessionsQuery.isError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>Не удалось загрузить сессии</AlertDescription>
      </Alert>
    );
  }

  const items = sessionsQuery.data?.items ?? [];
  if (items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">Нет активных сессий</p>
    );
  }

  return (
    <div className="space-y-2">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <ul className="space-y-2">
        {items.map((s) => {
          const isMobile = /mobile|iphone|android/i.test(s.user_agent ?? '');
          const Icon = isMobile ? Smartphone : Monitor;
          const label =
            s.device_name ||
            [s.browser, s.os].filter(Boolean).join(' · ') ||
            'Неизвестное устройство';
          return (
            <li
              key={s.id}
              className="flex items-start gap-2 p-2 rounded-md border border-border bg-card"
            >
              <Icon className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <p className="text-sm font-medium text-foreground truncate" title={label}>
                    {label}
                  </p>
                  {s.is_current_session && (
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                      Текущая
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {s.ip ?? '—'} · активна {formatRelative(s.last_active_at)}
                </p>
              </div>
              {!s.is_current_session && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  onClick={() => handleRevoke(s)}
                  disabled={revokingId === s.id}
                  aria-label="Завершить сессию"
                  title="Завершить сессию"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </li>
          );
        })}
      </ul>

      {revokeAllBlock}
    </div>
  );
}

function formatRelative(iso: string): string {
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const minutes = Math.floor(diffMs / 60_000);
    if (minutes < 1) return 'только что';
    if (minutes < 60) return `${minutes} мин назад`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} ч назад`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days} дн назад`;
    return d.toLocaleDateString('ru-RU');
  } catch {
    return iso;
  }
}
